import { useState } from "react";
import { getMatchVenueText } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/matchDetailDisplay";
import { bowlerOvers, cumAfter, personName, played, runRate, sortBowlers, wormPoints } from "./derive";
import type { CommVm, Inn, Phase } from "./derive";
import { Crest, NameLink, useBoxWidth } from "./shared";
import type { Ctx } from "./shared";
import { officialRows } from "../live/helpers";
import MatchVideos from "../media/MatchVideos";
import { useMatchMedia } from "../media/useMatchMedia";

/* ---------------- run progression ---------------- */

const INN_ORD = ["1st", "2nd", "3rd", "4th"];

const WormChart = ({ inns, comms, sel, setSel, multi, phases }: { inns: Inn[]; comms: (CommVm | null)[]; sel: number | null; setSel: (n: number) => void; multi: boolean; phases: Phase[] }) => {
  const [boxRef, boxW] = useBoxWidth();
  const Wd = Math.max(boxW || 820, 300), H = boxW && boxW < 500 ? 240 : 280, pl = 40, pr = 14, pt = 18, pb = 32;
  // limited overs: the over limit is the natural axis (20, 50...). Multi-day: the limit is huge (450), so size the axis to the overs actually bowled.
  const bowled = Math.max(...inns.map((i) => Math.ceil(i.legal / 6)), 1);
  const rawMax = multi ? bowled : Math.max(...inns.map((i) => Math.max(i.maxOvers, Math.ceil(i.legal / 6))), 1);
  const stepOv = rawMax <= 20 ? 2 : rawMax <= 50 ? 5 : rawMax <= 130 ? 10 : 20;
  const bands = multi ? [] : phases.filter((p) => p.named && (p.key !== "mid" && p.key !== "death"));
  const maxOv = multi ? Math.max(stepOv, Math.ceil(rawMax / stepOv) * stepOv) : rawMax;
  const target = !multi && inns.length === 2 ? inns[0].runs + 1 : null;
  const maxRuns = Math.max(...inns.map((i) => i.runs), target ?? 0, 1);
  const step = maxRuns > 900 ? 200 : maxRuns > 480 ? 100 : maxRuns > 240 ? 60 : 30;
  const mx = Math.ceil(maxRuns / step) * step;
  const X = (o: number) => pl + (o / maxOv) * (Wd - pl - pr);
  const Y = (v: number) => pt + (1 - v / mx) * (H - pt - pb);
  const tickStep = stepOv;
  const grid: number[] = [];
  for (let v = 0; v <= mx; v += step) grid.push(v);
  const ticks: number[] = [];
  for (let o = 0; o <= maxOv; o += tickStep) ticks.push(o);
  const hits = Array.from({ length: maxOv }, (_, i) => i + 1);
  const hitW = X(1) - X(0);

  return (
    <div ref={boxRef} className="chscroll">
    <svg className="ch" style={{ width: Wd, height: H }} viewBox={`0 0 ${Wd} ${H}`} role="img" aria-label="Run progression for both innings">
      {grid.map((v) => (
        <g key={v}>
          <line x1={pl} x2={Wd - pr} y1={Y(v)} y2={Y(v)} stroke="#EFF0F3" />
          <text x={pl - 8} y={Y(v) + 4} textAnchor="end" fontSize="11" fill="#9AA1AE">{v}</text>
        </g>
      ))}
      {ticks.map((o) => (
        <text key={o} x={X(o)} y={H - 10} textAnchor="middle" fontSize="11" fill="#687080">{o}</text>
      ))}
      {/* phase bands: T20 powerplay (overs 1-6), 50-over P1 / P2 / P3. Nothing for other formats and never for Test / multi-day. */}
      {bands.map((p, i) => (
        <g key={p.key}>
          <rect x={X(p.from)} y={pt} width={X(p.to) - X(p.from)} height={H - pt - pb} fill="#29276B" fillOpacity={bands.length === 1 || i % 2 === 0 ? 0.045 : 0.015} />
          <text x={X((p.from + p.to) / 2)} y={H - pb - 8} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#9AA1AE">{p.short.toUpperCase()}</text>
        </g>
      ))}
      {target != null && (
        <>
          <line x1={pl} x2={Wd - pr} y1={Y(target)} y2={Y(target)} stroke="#687080" strokeDasharray="5 5" />
          <text x={pl + 6} y={Y(target) - 6} fontSize="11" fontWeight="700" fill="#687080">Target {target}</text>
        </>
      )}
      {inns.map((inn, k) => {
        const comm = comms[k];
        if (!comm) return null;
        const pts = wormPoints(comm, inn);
        return (
          <g key={inn.id}>
            <path d={pts.map((p, i) => `${i ? "L" : "M"}${X(p.x).toFixed(1)} ${Y(p.y).toFixed(1)}`).join(" ")} fill="none" stroke={inn.team.color} strokeWidth="2.6" strokeLinejoin="round" strokeDasharray={multi && k >= 2 ? "7 5" : undefined} />
            {comm.wicketPoints.map((w, i) => (
              <circle key={i} cx={X(w.x)} cy={Y(w.y)} r="5" fill="#fff" stroke={inn.team.color} strokeWidth="2.2">
                <title>{w.label}</title>
              </circle>
            ))}
          </g>
        );
      })}
      {sel != null && (
        <>
          <line x1={X(sel)} x2={X(sel)} y1={pt} y2={H - pb} stroke="#15171C" strokeOpacity=".25" />
          {inns.map((inn, k) => {
            const c = cumAfter(comms[k], sel);
            return c ? <circle key={inn.id} cx={X(sel)} cy={Y(c.runs)} r="4" fill={inn.team.color} /> : null;
          })}
        </>
      )}
      {hits.map((o) => (
        <rect
          key={o}
          className="hit"
          x={X(o - 0.5)}
          y={pt}
          width={hitW}
          height={H - pt - pb}
          fill="transparent"
          tabIndex={0}
          role="button"
          aria-label={`After over ${o}`}
          onMouseEnter={() => setSel(o)}
          onFocus={() => setSel(o)}
          onClick={() => setSel(o)}
        />
      ))}
    </svg>
    </div>
  );
};

/* ---------------- team comparison ---------------- */

type CmpRow = { label: string; a: number; b: number; fmt: (v: number) => string };

const buildCmp = (allInns: Inn[], allComms: (CommVm | null)[], multiDay: boolean, phases: Phase[]): CmpRow[] => {
  const keep = allInns.map((_, i) => i).filter((i) => played(allInns[i]));
  const inns = keep.map((i) => allInns[i]);
  const comms = keep.map((i) => allComms[i]);
  if (inns.length < 2) return [];
  const f0 = (v: number) => String(v);
  const sumBat = (list: Inn[], k: "fours" | "six" | "dotBall") => list.reduce((t, i) => t + i.batting.reduce((u, x) => u + (x[k] ?? 0), 0), 0);
  if (multiDay) {
    // multi-innings (Test / two-innings-a-side): compare the two sides over all of their innings
    const A = inns[0].team.name;
    const aI = inns.filter((i) => i.team.name === A), bI = inns.filter((i) => i.team.name !== A);
    const tot = (l: Inn[], f: (i: Inn) => number) => l.reduce((t, i) => t + f(i), 0);
    const rows: CmpRow[] = [
      { label: "Total runs", a: tot(aI, (i) => i.runs), b: tot(bI, (i) => i.runs), fmt: f0 },
      { label: "Run rate", a: runRate(tot(aI, (i) => i.runs), tot(aI, (i) => i.legal)), b: runRate(tot(bI, (i) => i.runs), tot(bI, (i) => i.legal)), fmt: (v) => v.toFixed(2) },
    ];
    const maxN = Math.max(aI.length, bI.length);
    for (let n = 0; n < maxN; n++) if ((aI[n]?.runs ?? 0) + (bI[n]?.runs ?? 0) > 0) rows.push({ label: `${["1st", "2nd", "3rd", "4th"][n] ?? `${n + 1}th`} innings`, a: aI[n]?.runs ?? 0, b: bI[n]?.runs ?? 0, fmt: f0 });
    if (aI.every((i) => i.batting.length) && bI.every((i) => i.batting.length)) {
      rows.push({ label: "Fours", a: sumBat(aI, "fours"), b: sumBat(bI, "fours"), fmt: f0 });
      rows.push({ label: "Sixes", a: sumBat(aI, "six"), b: sumBat(bI, "six"), fmt: f0 });
      if (sumBat(aI, "dotBall") + sumBat(bI, "dotBall") > 0) rows.push({ label: "Dot balls", a: sumBat(aI, "dotBall"), b: sumBat(bI, "dotBall"), fmt: f0 });
    }
    rows.push({ label: "Extras received", a: tot(aI, (i) => i.extras.t), b: tot(bI, (i) => i.extras.t), fmt: f0 });
    rows.push({ label: "Wickets lost", a: tot(aI, (i) => i.wkts), b: tot(bI, (i) => i.wkts), fmt: f0 });
    return rows;
  }
  const [a, b] = inns;
  const rows: CmpRow[] = [{ label: "Run rate", a: runRate(a.runs, a.legal), b: runRate(b.runs, b.legal), fmt: (v) => v.toFixed(2) }];
  // runs in each named phase (T20: powerplay; 50 overs: P1 / P2 / P3). Test / multi-day never reaches this branch.
  const upto = (c: CommVm | null, n: number) => (n <= 0 ? 0 : [...(c?.overs ?? [])].reverse().find((o) => o.n <= n)?.cumRuns ?? 0);
  phases.filter((p) => p.named && (p.key === "pp" || p.key === "p1" || p.key === "p2" || p.key === "p3")).forEach((p) => {
    if (!comms[0] || !comms[1]) return;
    rows.push({ label: `${p.short} runs`, a: upto(comms[0], p.to) - upto(comms[0], p.from), b: upto(comms[1], p.to) - upto(comms[1], p.from), fmt: f0 });
  });
  const hasBat = a.batting.length > 0 && b.batting.length > 0;
  if (hasBat) {
    rows.push({ label: "Fours", a: sumBat([a], "fours"), b: sumBat([b], "fours"), fmt: f0 });
    rows.push({ label: "Sixes", a: sumBat([a], "six"), b: sumBat([b], "six"), fmt: f0 });
    if (sumBat([a], "dotBall") + sumBat([b], "dotBall") > 0) rows.push({ label: "Dot balls", a: sumBat([a], "dotBall"), b: sumBat([b], "dotBall"), fmt: f0 });
  }
  rows.push({ label: "Extras received", a: a.extras.t, b: b.extras.t, fmt: f0 });
  rows.push({ label: "Wickets lost", a: a.wkts, b: b.wkts, fmt: f0 });
  return rows;
};

/* ---------------- pane ---------------- */

const SummaryPane = ({ ctx }: { ctx: Ctx }) => {
  const { match, inns, comms, openPlayer, go } = ctx;
  const videos = useMatchMedia(match.matchId ?? "");
  const [sel, setSel] = useState<number | null>(null);
  const idxs = inns.map((_, i) => i).filter((i) => played(inns[i]));
  const wInns = idxs.map((i) => inns[i]);
  const wComms = idxs.map((i) => comms[i]);
  const hasWorm = comms.length === inns.length && wComms.every(Boolean) && wInns.length > 0;
  const multiDay = ctx.multiDay;
  const cmp = buildCmp(inns, comms, multiDay, ctx.phases);

  const caption = (() => {
    if (!hasWorm || wInns.length < 2) return null;
    if (multiDay) {
      const o = sel ?? Math.max(1, Math.floor(wInns[0].legal / 6));
      return (<><b>After {o} overs of each innings:</b> {wInns.map((inn, k) => { const c = cumAfter(wComms[k], o); return `${inn.team.code} ${INN_ORD[k] ?? k + 1} ${c ? `${c.runs}/${c.wkts}` : `${inn.runs}/${inn.wkts} (finished)`}`; }).join(" · ")}.</>);
    }
    const o = sel ?? Math.max(1, Math.floor(wInns[1].legal / 6));
    const a = cumAfter(wComms[0], o);
    const b = cumAfter(wComms[1], o);
    if (!a) return null;
    const diff = b ? b.runs - a.runs : 0;
    return (
      <>
        <b>After {o} overs:</b> {wInns[0].team.name} {a.runs}/{a.wkts}
        {b ? (
          <>
            , {wInns[1].team.name} {b.runs}/{b.wkts}.{" "}
            {diff > 0 ? <>{wInns[1].team.name} <b>{diff} ahead</b>.</> : diff < 0 ? <>{wInns[1].team.name} <b>{-diff} behind</b>.</> : "Level."}
          </>
        ) : (
          <>. {wInns[1].team.name} had already finished.</>
        )}
      </>
    );
  })();

  const infoRows: [string, string][] = [];
  const add = (k: string, v?: string | null) => {
    if (v && v.trim() && v !== "-") infoRows.push([k, v]);
  };
  const start = match.startDate ? new Date(match.startDate) : null;
  const validStart = start && !Number.isNaN(start.getTime()) ? start : null;
  add("Tournament", match.tournament?.tournamentName);
  add("League", match.leagueName);
  add("Format", [match.matchType ? match.matchType.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : "", inns[0]?.maxOvers && !ctx.multiDay ? `${inns[0].maxOvers} overs` : ""].filter(Boolean).join(", "));
  add("Venue", getMatchVenueText(match));
  add("Date", validStart?.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }));
  add("Start", validStart?.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }));
  add("Toss", match.tossResult);
  officialRows(match).forEach(([k, v]) => add(k, v));
  add("Pitch", match.pitchType);

  return (
    <section className="pane on" id="pane-summary" role="tabpanel" aria-labelledby="tab-summary">
      <div className="cols">
        <div className="col">
          <MatchVideos videos={videos} live={false} ended />
          {inns.length > 0 && (
            <div className="box">
              <div className="box-h">
                <h3>Innings summary</h3>
                <span className="sub">Top performers from each side</span>
                <div className="r"><button type="button" className="link" onClick={() => go("scorecard")}>Full scorecard</button></div>
              </div>
              <div className="isum">
                {inns.filter(played).map((i) => {
                  const bats = [...i.batting].sort((a, b) => (b.run ?? 0) - (a.run ?? 0)).slice(0, 3);
                  const bowls = sortBowlers(i.bowling).slice(0, 3);
                  return (
                    <div key={i.id}>
                      <div className="ih">
                        <Crest team={i.team} className="chip-c" />
                        <b>{i.team.name}</b>
                        <span className="n">{i.runs}/{i.wkts}<small>{Math.floor(i.legal / 6)}.{i.legal % 6} ov</small></span>
                      </div>
                      {bats.length > 0 && <div className="ilbl">Batting</div>}
                      {bats.map((b) => (
                        <div className="prow2" key={b.playerId}>
                          <span className="nm">
                            <NameLink id={b.playerId} onOpen={openPlayer}>{personName(b.playerFirstName, b.playerLastName)}</NameLink>
                            {/not\s*out/i.test(b.outString ?? "") ? "*" : ""}
                            {ctx.potmId && ctx.potmId === b.playerId && <span className="tag star">POTM</span>}
                          </span>
                          <span className="v">{b.run} <small>({b.balls})</small></span>
                        </div>
                      ))}
                      {bowls.length > 0 && <div className="ilbl">Bowling, {i.opp.name}</div>}
                      {bowls.map((b) => (
                        <div className="prow2" key={b.playerId}>
                          <span className="nm"><NameLink id={b.playerId} onOpen={openPlayer}>{personName(b.playerFirstName, b.playerLastName)}</NameLink></span>
                          <span className="v">{b.wickets ?? 0}/{b.runs ?? 0} <small>({bowlerOvers(b)})</small></span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {hasWorm && (
            <div className="box">
              <div className="box-h">
                <h3>Run progression</h3>
                <span className="sub">Cumulative runs by over. Tap or hover an over to compare.</span>
              </div>
              <div style={{ padding: "10px 18px 4px" }}>
                <WormChart inns={wInns} comms={wComms} sel={sel} setSel={setSel} multi={multiDay} phases={ctx.phases} />
              </div>
              <div className="legend">
                {wInns.map((i, k) => (
                  <span key={i.id}><i style={{ background: i.team.color }} />{i.team.name}{multiDay ? ` · ${INN_ORD[k] ?? k + 1} innings` : ""}</span>
                ))}
                <span><i className="dot" />Wicket</span>
                {!multiDay && wInns.length === 2 && <span><i style={{ background: "transparent", borderTop: "2px dashed #687080", height: 0 }} />Target</span>}
              </div>
              {caption && <div className="cap">{caption}</div>}
            </div>
          )}

          {comms.some((c) => c && c.moments.length > 0) && (
            <div className="box">
              <div className="box-h">
                <h3>Key moments</h3>
                <span className="sub">Wickets and turning points</span>
                <div className="r"><button type="button" className="link" onClick={() => go("commentary")}>Full commentary</button></div>
              </div>
              <ul className="tl">
                {inns.map((i, k) => {
                  const c = comms[k];
                  if (!c) return null;
                  return [
                    <li key={`h${i.id}`} className="ih2">{i.team.name} innings</li>,
                    ...c.moments.map((m, j) => (
                      <li key={`${i.id}-${j}`} className={m.kind}>
                        <span className="ov">{m.at}</span>
                        <span className="tx"><b>{m.bold}</b>{m.text}</span>
                      </li>
                    )),
                  ];
                })}
              </ul>
            </div>
          )}
        </div>

        <aside className="col rail">
          {cmp.length > 0 && (
            <div className="box">
              <div className="box-h"><h3>Team comparison</h3></div>
              <div className="cmp">
                <div className="hd">
                  <span className="c"><Crest team={inns[0].team} className="chip-c" />{inns[0].team.code}</span>
                  <span />
                  <span className="c" style={{ justifyContent: "flex-end" }}>{inns[0].opp.code}<Crest team={inns[0].opp} className="chip-c" /></span>
                </div>
                {cmp.map((r) => {
                  const m = Math.max(r.a, r.b) || 1;
                  return (
                    <div className="row" key={r.label}>
                      <div className="lab"><b>{r.fmt(r.a)}</b><span>{r.label}</span><b>{r.fmt(r.b)}</b></div>
                      <div className="bars">
                        <div><i style={{ width: `${(r.a / m) * 100}%`, background: inns[0].team.color }} /></div>
                        <div><i style={{ width: `${(r.b / m) * 100}%`, background: inns[0].opp.color }} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {infoRows.length > 0 && (
            <div className="box">
              <div className="box-h"><h3>Match info</h3></div>
              <dl className="dl">
                {infoRows.map(([k, v]) => (
                  <div key={k} style={{ display: "contents" }}><dt>{k}</dt><dd>{v}</dd></div>
                ))}
              </dl>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
};

export default SummaryPane;
